import { useMemo, useState } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import {
  Loader2,
  Activity as ActivityIcon,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Video,
  CreditCard,
  GitCommit,
  LifeBuoy,
  UserPlus,
  Workflow,
} from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'

type Story = {
  aggregationKey: string
  kind: string
  summary: string
  startTime: number
  endTime: number
  eventCount: number
  threadId?: string
  sampleTypes: string[]
}

type EventDetail = {
  type: string
  timestamp: number
  title: string
  subtitle?: string
  threadId?: string
  table?: string
  docId?: string
  actorType?: string
  actorId?: string
  data?: any
}

const KIND_STYLE: Record<string, { icon: typeof MessageSquare; tone: string }> = {
  message: { icon: MessageSquare, tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  video: { icon: Video, tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  payment: { icon: CreditCard, tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  deploy: { icon: GitCommit, tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  support: { icon: LifeBuoy, tone: 'bg-rose-50 text-rose-700 border-rose-200' },
  user: { icon: UserPlus, tone: 'bg-blue-50 text-blue-700 border-blue-200' },
}

const FALLBACK_STYLE = { icon: Workflow, tone: 'bg-neutral-50 text-neutral-600 border-neutral-200' }

const TYPE_PREFIXES = ['all', 'message', 'video', 'stripe', 'github', 'railway', 'support', 'user', 'session'] as const
const WINDOW_OPTIONS = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: '7d', hours: 168 },
]

function formatRelative(ts: number): string {
  const delta = Date.now() - ts
  const min = Math.round(delta / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.round(hr / 24)
  return `${day}d ago`
}

function formatTimeRange(start: number, end: number): string {
  const fmt = (ts: number) =>
    new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  if (start === end) return fmt(start)
  return `${fmt(start)} → ${fmt(end)}`
}

function StoryRow({ story }: { story: Story }) {
  const [open, setOpen] = useState(false)
  const style = KIND_STYLE[story.kind] ?? FALLBACK_STYLE
  const { icon: Icon, tone } = style

  const events = useQuery(
    api.activity.openStory,
    open
      ? {
          aggregationKey: story.aggregationKey,
          startTime: story.startTime,
          endTime: story.endTime,
        }
      : 'skip',
  )

  return (
    <div className="border-b border-neutral-100 last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-neutral-50 transition-colors"
      >
        <div className="mt-0.5 shrink-0">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-neutral-400" /> : <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />}
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wider mt-0.5 shrink-0',
            tone,
          )}
        >
          <Icon className="h-3 w-3" strokeWidth={2} />
          {story.kind}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-neutral-900">{story.summary}</div>
          <div className="flex items-center gap-2 mt-0.5 text-[11px] text-neutral-500">
            <span>{story.eventCount} event{story.eventCount === 1 ? '' : 's'}</span>
            <span className="text-neutral-300">·</span>
            <span>{formatRelative(story.endTime)}</span>
            <span className="text-neutral-300">·</span>
            <span>{formatTimeRange(story.startTime, story.endTime)}</span>
            {story.threadId && (
              <>
                <span className="text-neutral-300">·</span>
                <span className="font-mono text-neutral-400 truncate">{story.threadId}</span>
              </>
            )}
          </div>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pl-12">
          {events === undefined ? (
            <div className="flex items-center gap-2 text-[11px] text-neutral-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading events…
            </div>
          ) : events.length === 0 ? (
            <div className="text-[11px] text-neutral-400">No events.</div>
          ) : (
            <div className="rounded-md border border-neutral-200 bg-neutral-50/40 divide-y divide-neutral-100">
              {events.map((e: EventDetail, i) => (
                <div key={`${e.type}-${e.timestamp}-${i}`} className="px-3 py-2 text-[12px]">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[10px] text-neutral-400 shrink-0">
                      {new Date(e.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                    </span>
                    <span className="font-mono text-[10px] px-1 py-0.5 rounded bg-white border border-neutral-200 text-neutral-600 shrink-0">
                      {e.type}
                    </span>
                    <span className="text-neutral-800 truncate">{e.title}</span>
                  </div>
                  {e.subtitle && (
                    <div className="text-[11px] text-neutral-500 mt-0.5 ml-[64px]">{e.subtitle}</div>
                  )}
                  {e.data && (
                    <details className="mt-1 ml-[64px]">
                      <summary className="text-[10px] text-neutral-400 cursor-pointer hover:text-neutral-600">
                        data
                      </summary>
                      <pre className="text-[10px] text-neutral-500 mt-1 whitespace-pre-wrap break-all font-mono">
                        {JSON.stringify(e.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function AdminActivityPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin

  const [windowHours, setWindowHours] = useState(48)
  const [typeFilter, setTypeFilter] = useState<(typeof TYPE_PREFIXES)[number]>('all')
  const [threadFilter, setThreadFilter] = useState('')

  const result = useQuery(
    api.activity.getAggregatedTimeline,
    isAdmin
      ? {
          windowHours,
          typePrefix: typeFilter === 'all' ? undefined : typeFilter,
          threadId: threadFilter.trim() || undefined,
        }
      : 'skip',
  )

  const groupedByKind = useMemo(() => {
    if (!result) return {} as Record<string, number>
    const counts: Record<string, number> = {}
    for (const s of result.stories) {
      counts[s.kind] = (counts[s.kind] ?? 0) + 1
    }
    return counts
  }, [result])

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-neutral-900">Activity</h1>
        <p className="text-sm text-neutral-500 mt-0.5">
          Unified firehose: messages, videos, payments, deploys, support, signups. Grouped into stories.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-neutral-200 bg-white p-0.5">
          {WINDOW_OPTIONS.map((w) => (
            <button
              key={w.hours}
              onClick={() => setWindowHours(w.hours)}
              className={cn(
                'px-2.5 py-1 text-[11px] font-medium rounded transition-colors',
                windowHours === w.hours ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-100',
              )}
            >
              {w.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 rounded-md border border-neutral-200 bg-white p-0.5 overflow-x-auto">
          {TYPE_PREFIXES.map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={cn(
                'px-2.5 py-1 text-[11px] font-medium rounded transition-colors capitalize',
                typeFilter === t ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-100',
              )}
            >
              {t}
            </button>
          ))}
        </div>

        <input
          type="text"
          value={threadFilter}
          onChange={(e) => setThreadFilter(e.target.value)}
          placeholder="threadId (workflow:..., stripe:..., slack:...)"
          className="px-3 py-1.5 text-[11px] rounded-md border border-neutral-200 bg-white font-mono w-72 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {result === undefined ? (
        <div className="flex items-center justify-center py-16 rounded-lg border border-neutral-200 bg-white">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-300" />
        </div>
      ) : result.stories.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50/50 px-4 py-12 text-center text-sm text-neutral-400">
          <ActivityIcon className="h-5 w-5 text-neutral-300 mx-auto mb-2" />
          No activity in this window.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 text-[11px] text-neutral-500">
            <span>
              {result.totalEvents} events · {result.stories.length} stories
            </span>
            {Object.entries(groupedByKind).map(([kind, count]) => {
              const style = KIND_STYLE[kind] ?? FALLBACK_STYLE
              return (
                <span
                  key={kind}
                  className={cn('inline-flex items-center px-1.5 py-0.5 rounded border text-[10px]', style.tone)}
                >
                  {kind}: {count}
                </span>
              )
            })}
          </div>

          <div className="rounded-lg border border-neutral-200 bg-white">
            {result.stories.map((s) => (
              <StoryRow key={s.aggregationKey} story={s} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
